import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MatriculaAlunoSchema, tipoMatriculaOptions } from '@/schema/matriculaAlunoSchema';
import { Select, Divider, Row, Col, DatePicker, Radio } from "antd";
import { brazilStates } from '@/utils/staticData';
import { getEnderecoByCep } from '@/services/enderecoByCep';
import { getByCpf } from '@/services/pessoa';
import { Input } from '@/components/ui/inputForm';
import { MaskedInput, createDefaultMaskGenerator } from 'react-hook-mask';
import dayjs from 'dayjs';

import 'dayjs/locale/pt-br';
import { Container } from '../styles';
import moment from 'moment';
import { parseAnyToDayjs, formatToBr } from '@/utils/dateUtils';
import cpfValidation from '@/utils/cpfValidation';
import { errorHandler } from '@/utils/errorhandler';
import { preencherPessoaNoForm, temValor } from '../preencherPessoa';

dayjs.locale('pt-br');

const maskCelular = createDefaultMaskGenerator('(99) 99999-9999');
const maskTelefone = createDefaultMaskGenerator('(99) 9999-9999');
const maskCPF = createDefaultMaskGenerator('999.999.999-99');

type Props = {
  current: number;
};

const Responsavel: React.FC<Props> = ({ current }) => {

  const [blockedFields, setBlockedFields] = useState(false);
  const cpfUserInteraction = useRef(false);
  const ultimoCpfConsultado = useRef<string | null>(null);
  const [dataNascimentoResponsavel, setDataNascimentoResponsavel] = useState<dayjs.Dayjs | null>(null);

  const {
    register,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    control
  } = useFormContext<MatriculaAlunoSchema>();

  const responsavelErros = errors.responsavel;

  const { Option } = Select;

  const values = getValues();

  useEffect(() => {
    // Inicializar data de nascimento se já existir no formulário
    const dataExistente = getValues("responsavel.pessoaResponsavel.dataNascimento");
    if (dataExistente) {
      setDataNascimentoResponsavel(parseAnyToDayjs(dataExistente));
    }
  }, [values, responsavelErros])

  function verifyErrorRgState(): any {
    if (responsavelErros?.pessoaResponsavel?.ufRg) return 'O Estado de Emissão é obrigatório';
    return null;
  }

  function verifyErrorAddressState(): string | null {
    if (responsavelErros?.pessoaResponsavel?.uf) return 'O Estado é obrigatório';
    return null;
  }

  const getBuscaCEP = async (e: { target: { value: string } }) => {
    const cep = e.target.value.replace(/\D/g, '');
    if (!cep) {
      return;
    }
    const data: any = await getEnderecoByCep(cep);
    if (!data?.data?.cep) {
      return;
    }
    setValue("responsavel.pessoaResponsavel.cep", data.data.cep);
    setValue("responsavel.pessoaResponsavel.uf", data.data.uf);
    setValue("responsavel.pessoaResponsavel.cidade", data.data.localidade);
    setValue("responsavel.pessoaResponsavel.bairro", data.data.bairro);
    setValue("responsavel.pessoaResponsavel.logradouro", data.data.logradouro);
  };

  // Função para consultar pessoa por CPF
  const consultarPessoaPorCpf = async (cpf: string) => {
    // Limpar máscara do CPF
    const cpfLimpo = cpf.replace(/\D/g, '');

    // Verificar se o CPF tem 11 dígitos
    if (cpfLimpo.length !== 11) {
      return;
    }

    // O MaskedInput dispara onChange a cada tecla: sem isto, corrigir um dígito
    // de um CPF já completo refazia a consulta e reaplicava o preenchimento.
    if (ultimoCpfConsultado.current === cpfLimpo) {
      return;
    }
    ultimoCpfConsultado.current = cpfLimpo;

    try {
      const alunoResp = await getByCpf(cpfLimpo, 2);
      if (alunoResp?.status === 200 && alunoResp.data) {
        const aluno = alunoResp.data;
        preencherResponsavel(aluno?.responsavel);
        preencherEstudante(aluno?.aluno);
      }
    } catch (error: any) {
      // CPF sem registro na base é caso normal, não erro: a tela fica exatamente
      // como está e a Pessoa é gravada no submit. Só falha de verdade avisa.
      const status = error?.response?.status;
      if (status === 404 || status === 204) {
        return;
      }
      errorHandler(error);
    }
  };

  const preencherResponsavel = (pessoa: any) => {
    if (!pessoa) return;

    // O CPF fica de fora: é o que o usuário acabou de digitar nesta tela.
    const { dataNascimento, aplicados } = preencherPessoaNoForm(
      setValue,
      'responsavel.pessoaResponsavel',
      pessoa,
      { ignorar: ['cpfCnpj'] },
    );

    // Profissão e grau de parentesco são do Aluno, não da Pessoa.
    if (temValor(pessoa.profissao)) {
      setValue('responsavel.profissao', String(pessoa.profissao));
    }
    if (temValor(pessoa.grauParentesco)) {
      setValue('responsavel.grauParentesco', String(pessoa.grauParentesco));
    }

    if (aplicados.celular) setCelularValue(aplicados.celular);
    if (aplicados.telefone) setTelefoneValue(aplicados.telefone);
    if (dataNascimento) setDataNascimentoResponsavel(dataNascimento);
  };

  const preencherEstudante = (pessoa: any) => {
    if (!pessoa) return;
    preencherPessoaNoForm(setValue, 'estudante.pessoaEstudante', pessoa);
  };

  function setCelularValue(celular: any){
    setValue('responsavel.pessoaResponsavel.celular', celular);
  }

  function setTelefoneValue(telefone: any){
    setValue('responsavel.pessoaResponsavel.telefone', telefone);
  }

  function setCpfValue(cpf: any){
    setValue('responsavel.pessoaResponsavel.cpfCnpj', cpf);

    const cpfDigits = (cpf || '').replace(/\D/g, '');
    if (cpfDigits.length === 0) {
      clearErrors('responsavel.pessoaResponsavel.cpfCnpj');
      return;
    }
    if (cpfDigits.length === 11) {
      if (!cpfValidation(cpfDigits)) {
        setError('responsavel.pessoaResponsavel.cpfCnpj', { type: 'manual', message: 'CPF inválido' });
        return;
      }
      clearErrors('responsavel.pessoaResponsavel.cpfCnpj');
      if (cpfUserInteraction.current) {
        consultarPessoaPorCpf(cpf);
      }
    } else {
      clearErrors('responsavel.pessoaResponsavel.cpfCnpj');
    }
  }

  function setDataNascimentoValue(dataNascimento: dayjs.Dayjs | null){
    setDataNascimentoResponsavel(dataNascimento);
    setValue('responsavel.pessoaResponsavel.dataNascimento', formatToBr(dataNascimento));
  }

  const SelectUF: React.FC<{ registerPath: any, blocked: boolean, errorMessage: string | null }> = ({ registerPath, blocked, errorMessage }) => {
    return <>
      <Controller
        control={control}
        name={registerPath}
        render={({ field }) => {
          return (
            <div>
              <Select
                {...field}
                allowClear
                showSearch
                disabled={blocked}
                placeholder="Selecione o Estado"
                style={{ width: '100%', height: '2.5rem' }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
              >
                {brazilStates.map((item: any) => (
                  <Option
                    key={item?.label}
                    value={item?.label}
                  >
                    {item?.value}
                  </Option>
                ))}
              </Select>
            </div>
          );
        }} />
      {errorMessage && <span className='text-red-500 text-sm'>{errorMessage}</span>}
    </>
  }


  return (
    <>
      <Container>
        <Row gutter={[16, 24]}>
          <Col span={24}>
            <label className="block mb-2">Tipo de Matrícula</label>
            <Controller
              control={control}
              name="tipoMatricula"
              render={({ field }) => (
                <>
                  <Radio.Group {...field} size="large">
                    {tipoMatriculaOptions.map((option) => (
                      <Radio.Button 
                        key={option.value}
                        value={option.value}
                        style={{
                          fontWeight: field.value === option.value ? 'bold' : 'normal',
                          opacity: field.value === option.value ? 1 : 0.5,
                        }}
                      >
                        {option.label}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                  {errors?.tipoMatricula && (
                    <div className='text-red-500 text-sm mt-1'>
                      {errors.tipoMatricula.message}
                    </div>
                  )}
                </>
              )}
            />
          </Col>
        </Row>

        <Divider />

        <Row gutter={[16, 24]}>
          <Col md={24} lg={6}>
            <label>CPF</label>
              <MaskedInput
              maskGenerator={maskCPF}
              value={values?.responsavel?.pessoaResponsavel?.cpfCnpj || ''}
              onChange={setCpfValue}
              onFocus={() => { cpfUserInteraction.current = true; }}
              onBlur={() => { cpfUserInteraction.current = false; }}
            />
            {responsavelErros?.pessoaResponsavel?.cpfCnpj && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.cpfCnpj.message}</span>}
          </Col>
          <Col md={24} lg={18}>
            <label>Nome</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.nome')}
            />
            {responsavelErros?.pessoaResponsavel?.nome && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.nome.message}</span>}
          </Col>
        </Row>
        <Divider orientation="left">RG</Divider>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={8}>
            <label>Número</label>
            <Input
              type="number"
              disabled={blockedFields}
              placeholder='Somente números'
              {...register('responsavel.pessoaResponsavel.rg')}
            />
            {responsavelErros?.pessoaResponsavel?.rg && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.rg.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Emissor</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.orgaoEmissorRg')}
            />
            {responsavelErros?.pessoaResponsavel?.orgaoEmissorRg && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.orgaoEmissorRg.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Estado de Emissão</label>
            <SelectUF
              registerPath="responsavel.pessoaResponsavel.ufRg"
              blocked={blockedFields}
              errorMessage={verifyErrorRgState()}
            />
          </Col>
        </Row>
        <Divider orientation="left">Endereço</Divider>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={6}>
            <label>CEP</label>
            <Input
              type="text"
              disabled={blockedFields}
              placeholder='Somente números'
              {...register('responsavel.pessoaResponsavel.cep')}
              onBlur={getBuscaCEP}
            />
            {responsavelErros?.pessoaResponsavel?.cep && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.cep.message}</span>}
          </Col>
          <Col md={24} lg={6}>
            <label>Estado</label>
            <SelectUF
              registerPath="responsavel.pessoaResponsavel.uf"
              blocked={blockedFields}
              errorMessage={verifyErrorAddressState()}
            />
          </Col>
          <Col md={24} lg={12}>
            <label>Cidade</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.cidade')}
            />
            {responsavelErros?.pessoaResponsavel?.cidade && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.cidade.message}</span>}
          </Col>
        </Row>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={8}>
            <label>Bairro</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.bairro')}
            />
            {responsavelErros?.pessoaResponsavel?.bairro && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.bairro.message}</span>}
          </Col>
          <Col md={24} lg={12}>
            <label>Logradouro</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.logradouro')}
            />
            {responsavelErros?.pessoaResponsavel?.logradouro && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.logradouro.message}</span>}
          </Col>
          <Col md={24} lg={4}>
            <label>Número</label>
            <Input
              type="number"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.numero')}
            />
            {responsavelErros?.pessoaResponsavel?.numero && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.numero.message}</span>}
          </Col>
        </Row>
        <Row gutter={[16, 24]}>
          <Col md={24}>
            <label>Complemento</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.complemento')}
            />
            {responsavelErros?.pessoaResponsavel?.complemento && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.complemento.message}</span>}
          </Col>
        </Row>
        <Divider orientation="left">Contato</Divider>
        <Row gutter={[16, 24]}>
          <Col md={24} lg={8}>
            <label>E-mail</label>
            <Input
              type="email"
              disabled={blockedFields}
              {...register('responsavel.pessoaResponsavel.email')}
            />
            {responsavelErros?.pessoaResponsavel?.email && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.email.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Celular</label>
            <MaskedInput
              maskGenerator={maskCelular}
              value={values?.responsavel?.pessoaResponsavel?.celular ?? ''}
              onChange={setCelularValue}
              placeholder='(99) 99999-9999'
            />
            {responsavelErros?.pessoaResponsavel?.celular && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.celular.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Telefone</label>
            <MaskedInput
              maskGenerator={maskTelefone}
              value={values?.responsavel?.pessoaResponsavel?.telefone ?? ''}
              onChange={setTelefoneValue}
              placeholder='(99) 9999-9999'
            />
            {responsavelErros?.pessoaResponsavel?.telefone && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.telefone.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Data Nascimento</label>
            <DatePicker
              value={dataNascimentoResponsavel}
              onChange={setDataNascimentoValue}
              format="DD/MM/YYYY"
              placeholder="DD/MM/AAAA"
              disabled={blockedFields}
              style={{ width: '100%', height: '2.5rem' }}
            />
            {responsavelErros?.pessoaResponsavel?.dataNascimento && <span className='text-red-500 text-sm'>{responsavelErros.pessoaResponsavel.dataNascimento.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Profissão</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.profissao')}
            />
            {responsavelErros?.profissao && <span className='text-red-500 text-sm'>{responsavelErros.profissao.message}</span>}
          </Col>
          <Col md={24} lg={8}>
            <label>Grau de Parentesco</label>
            <Input
              type="text"
              disabled={blockedFields}
              {...register('responsavel.grauParentesco')}
            />
            {responsavelErros?.grauParentesco && <span className='text-red-500 text-sm'>{responsavelErros.grauParentesco.message}</span>}
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Responsavel;